import axios from "axios";
import {
  setBaseline,
  setPreview,
  setCurrent,
  setShowPreview,
  setAssemblyCompiling,
} from "../slices/assemblySlice";
import {
  makeAssemblyKey,
  computeAllocationsHash,
  computePeriodsHash,
} from "../../utils/assemblyKey";

// --- helpers ---

const toMask = (flags = []) =>
  Array.isArray(flags) ? flags.map((b) => (b ? "1" : "0")).join("") : String(flags || "");

const toObj = (mOrObj) => {
  if (mOrObj instanceof Map) return Object.fromEntries(mOrObj);
  return mOrObj || {};
};

function normalizeAssemblyResponse(input) {
  const data = input || {};

  if (!data.assembledBusinessCase && data?.scenario?.values) {
    data.assembledBusinessCase = data;
  }

  if (!data.assembledBusinessCase && data.businessCase) {
    data.assembledBusinessCase = data.businessCase;
    delete data.businessCase;
  }

  if (Array.isArray(data.parsedScenarios)) {
    data.parsedScenarios = data.parsedScenarios.map((s) => ({
      ...s,
      values: toObj(s.values),
      originalValues: toObj(s.originalValues),
      roll_up: toObj(s.roll_up),
    }));
  }

  const sc = data.assembledBusinessCase?.scenario;
  if (sc) {
    sc.values = toObj(sc.values);
    sc.originalValues = toObj(sc.originalValues);
    sc.roll_up = toObj(sc.roll_up);
  }

  return data;
}

// Frontend key is now only for local state organization.
// Freshness is determined by backend.
function buildMetaAndKey({
  bc,
  expandedMask,
  expandedPeriods,
  allocations,
  periods,
  accountStructure,
  pushback,
  includeAllocations = false,
}) {
  const mask = expandedMask ?? toMask(expandedPeriods);
  const bcId = Number(bc?.business_case_id);

  const structureVersion =
    accountStructure?.version ??
    accountStructure?.updated_at ??
    [
      accountStructure?.balancesheet?.assets?.length ?? 0,
      accountStructure?.balancesheet?.liabilities?.length ?? 0,
      accountStructure?.balancesheet?.equities?.length ?? 0,
      accountStructure?.profitloss?.length ?? 0,
      accountStructure?.cashflow?.length ?? 0,
      accountStructure?.drivers?.length ?? 0,
      accountStructure?.custom?.length ?? 0,
    ].join("|");

  const allocForBc = Array.isArray(allocations)
    ? allocations.filter(
        (a) => Number(a?.business_case_id ?? a?.businessCaseId) === bcId
      )
    : [];

  const meta = {
    bcId,
    expandedMask: mask,
    pushback: pushback || undefined,
    allocationsHash: includeAllocations ? computeAllocationsHash(allocForBc) : "",
    periodsHash: computePeriodsHash(periods),
    structureVersion,
  };

  const key = makeAssemblyKey(meta);
  return { meta, key, mask, allocForBc };
}

// --- thunks ---

export const assembleBaseline =
  ({
    bc,
    periods,
    accountStructure,
    expandedPeriods,
    expandedMask,
    allocations,
    entities,
    force = false,
  }) =>
  async (dispatch) => {
    const { meta, key, mask } = buildMetaAndKey({
      bc,
      expandedPeriods,
      expandedMask,
      allocations,
      periods,
      accountStructure,
      includeAllocations: true,
    });

    dispatch(setAssemblyCompiling(true));

    try {
      const { data } = await axios.post("/assemble-business-case", {
        businessCase: bc,
        periods,
        accountStructure,
        expandedMask: mask,
        expandedPeriods,
        allocations: undefined,
        entities,
        force,
        includePreview: true,
      });

      const normalized = normalizeAssemblyResponse(data);

      dispatch(setBaseline(key, meta, normalized));
      dispatch(setCurrent(key));
      dispatch(setShowPreview(false));

      return { key, data: normalized, reused: false };
    } finally {
      dispatch(setAssemblyCompiling(false));
    }
  };

export const assemblePreview =
  ({
    bc,
    periods,
    accountStructure,
    expandedPeriods,
    expandedMask,
    allocations,
    pushback,
    entities,
    force = false,
  }) =>
  async (dispatch) => {
    const { meta, key, mask, allocForBc } = buildMetaAndKey({
      bc,
      expandedPeriods,
      expandedMask,
      allocations,
      periods,
      accountStructure,
      pushback,
      includeAllocations: true,
    });
    dispatch(setAssemblyCompiling(true));

    try {
      const { data } = await axios.post("/assemble-business-case", {
        businessCase: bc,
        periods,
        accountStructure,
        expandedMask: mask,
        expandedPeriods,
        allocations: allocForBc.length ? allocForBc : undefined,
        pushback,
        entities,
        force,
        includePreview: true,
      });

      const normalized = normalizeAssemblyResponse(data);

      dispatch(setPreview(key, meta, normalized));
      dispatch(setCurrent(key));
      dispatch(setShowPreview(true));

      return { key, data: normalized, reused: false };
    } finally {
      dispatch(setAssemblyCompiling(false));
    }
  };